import axios from "axios";
import { AppRoutes, backend_url, stripe_url } from "../constants";
import type { Client } from "@/types";
import { Endpoints } from "./api/endpoints";
import type { ReactNode } from "react";
import { CLIENT_EVENTS, clientEventSystem } from "./events/clientEvents";
import { useAuthStore } from "@utils/stores/authStore.ts";
import type { LoginResponse } from "@pages/LoginManagement/types/login.types.ts";

export const AXIOS_CONFIG = {
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  maxBodyLength: 25 * 1024 * 1024, // 25MB
  maxContentLength: 25 * 1024 * 1024, // 25MB
};

/* GET and POST: api/clients */
/**
 *
 * @param params {Object}
 * retrieves a list of clients for the logged in taxpro
 * sets the filtered clients,
 * sets the clients,
 * sets the personal info, and
 * sets the business info
 */
export const getClients = async (params?: any) => {
  try {
    // Convert camelCase frontend params to snake_case backend params
    const backendParams: any = {};

    if (params) {
      if (params.limit !== undefined) backendParams.limit = params.limit;
      if (params.page !== undefined) backendParams.page = params.page;
      // Only include status if it has a value
      if (
        params.status !== undefined &&
        params.status !== null &&
        params.status !== ""
      ) {
        backendParams.status = params.status;
      }
      // Only include order if it has a value
      if (params.order !== undefined && params.order !== "") {
        backendParams.order = params.order;
      }
      // Convert assignedTo array to comma-separated string
      // Handle "null" string for unassigned filter
      if (params.assignedTo !== undefined && params.assignedTo.length > 0) {
        // Check if filtering for unassigned clients
        const hasUnassignedFilter = params.assignedTo.includes("null");

        if (hasUnassignedFilter) {
          // Send "null" to backend to filter for unassigned clients
          backendParams.assigned_to = "null";
        } else {
          // Filter for specific assigned users
          const validAssignedTo = Array.isArray(params.assignedTo)
            ? params.assignedTo.filter(
                (id: string) => id !== "null" && id !== null && id !== undefined
              )
            : params.assignedTo;

          // Only include if we have valid IDs after filtering
          if (validAssignedTo.length > 0) {
            backendParams.assigned_to = validAssignedTo.join(",");
          }
        }
      }
      // Only include sortBy if it has a value
      if (params.sortBy !== undefined && params.sortBy !== "") {
        backendParams.sort_by = params.sortBy;
      }
      // Only include search if it has a value
      if (params.search !== undefined && params.search.trim() !== "") {
        backendParams.search = params.search;
      }
      if (params.includeArchived !== undefined)
        backendParams.include_archived = params.includeArchived;
      if (params.taxYear !== undefined && params.taxYear !== null)
        backendParams.tax_year = params.taxYear;
      // Only include organizerStatus if it has a value
      if (
        params.organizerStatus !== undefined &&
        params.organizerStatus !== null &&
        params.organizerStatus !== ""
      ) {
        backendParams.organizer_status = params.organizerStatus;
      }
      // Convert tagIds array to comma-separated string
      if (params.tagIds !== undefined && params.tagIds.length > 0) {
        const validTagIds = Array.isArray(params.tagIds)
          ? params.tagIds.filter(
              (id: string) => id !== null && id !== undefined && id !== ""
            )
          : params.tagIds;

        // Only include if we have valid IDs after filtering
        if (validTagIds.length > 0) {
          backendParams.tag_ids = validTagIds.join(",");
        }
      }
    }

    const response = await axios.get(
      `${backend_url}/${Endpoints.clients.root}`,
      {
        params: backendParams,
        ...AXIOS_CONFIG,
        paramsSerializer: {
          indexes: null,
        },
      }
    );
    const clientsToStore = Array.isArray(response.data.content.results)
      ? response.data.content.results
      : [response.data.content.results];
    return {
      clients: clientsToStore,
      next: response.data.content.next,
      previous: response.data.content.previous,
      totalClients: response.data.content.totalClients,
      totalPages: response.data.content.totalPages,
      availableTaxYears: response.data.content.availableTaxYears || [],
    };
  } catch (error) {
    console.error("Error fetching clients:", error);
    throw error;
  }
};

/**
 * get information on a single client
 */
export const getClient = async (userId: string) => {
  try {
    const response = await axios.get(
      `${backend_url}/${Endpoints.clients.root}${userId}/`,
      {
        ...AXIOS_CONFIG,
      }
    );
    return response.data.content;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/**
 * Get available tax years from client documents
 * @returns Array of tax years in descending order (most recent first)
 */
export const getTaxYears = async (): Promise<number[]> => {
  try {
    const response = await axios.get(`${backend_url}/${Endpoints.taxYears}`, {
      ...AXIOS_CONFIG,
    });
    return response.data.taxYears || [];
  } catch (error) {
    console.error("Error fetching tax years:", error);
    return [];
  }
};

/**
 *
 * @param content a JSON object
 * @param userId an id of a user
 * updates a client's information
 */
export const updateClients = async (
  updatedData: Partial<Client>,
  userId: string
): Promise<Client> => {
  try {
    const response = await axios.put<Client>(
      `${backend_url}/${Endpoints.clients.root}${userId}/`,
      updatedData,
      {
        ...AXIOS_CONFIG,
      }
    );

    if (response.status !== 200) {
      throw new Error(
        `Failed to update client. Status code: ${response.status}`
      );
    }

    // Check if the status is being updated
    const newStatus = updatedData.status ?? updatedData.taxInfo?.status;

    if (newStatus) {
      if (newStatus === "Archived") {
        // Emit the CLIENT_ARCHIVED event
        clientEventSystem.emit(CLIENT_EVENTS.CLIENT_ARCHIVED, userId);
      } else {
        // Emit the CLIENT_STATUS_CHANGED event for any status change
        clientEventSystem.emit(CLIENT_EVENTS.CLIENT_STATUS_CHANGED, userId);
      }
    }

    return response.data; // Return the updated Client object
  } catch (error: any) {
    console.error("Error updating client:", error);
    throw error; // Re-throw the error so that useMutation can handle it
  }
};

// GET and POST: api/owner
/**
 * a helper function to get taxpro information
 * @param businessId an id of a business,
 * if provided will set the business info in local storage
 */
export const getTaxProInfo = async (businessId?: string | null) => {
  // Create the base configuration object
  const config = {
    url: `${backend_url}/${Endpoints.owner}${
      businessId ? businessId + "/" : ""
    }`,
    method: "GET",
    ...AXIOS_CONFIG,
  };

  try {
    const response = await axios(config);

    sessionStorage.setItem("taxpro", JSON.stringify(response?.data?.content));

    if (businessId) {
      localStorage.setItem(
        "businessInfo",
        JSON.stringify(response?.data?.content)
      );
    }

    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/**
 *
 * @param content a JSON object
 * @returns a response that contains the status code, message, and data
 */
export const updateTaxProInfo = async (content: Record<string, any>) => {
  try {
    const response = await axios.put(
      `${backend_url}/${Endpoints.owner}`,
      content,
      {
        ...AXIOS_CONFIG,
      }
    );
    const key = response.data.content.updatedKeys[0];
    if (key in content) {
      // Get the current businessInfo from localStorage
      const businessInfo = JSON.parse(
        localStorage.getItem("businessInfo") ?? "{}"
      );

      // Update the specific key in businessInfo
      businessInfo[key] = content[key];

      // Save the updated businessInfo back to localStorage
      localStorage.setItem("businessInfo", JSON.stringify(businessInfo));
    }
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/* GET and POST: api/forms */

/**
 *
 * @param index index of the document in the clientDocs array
 * @param clientDocs array of a client's documents
 * @param userId a user id
 * @returns a presigned url in s3 for the document
 */
export const getPreSignedUrl = async (
  userId: string | undefined,
  fileName: string,
  form: string,
  taxYear: number
) => {
  try {
    const presignedUrl = await axios.get(
      `${backend_url}/${Endpoints.presignedUrl}`,
      {
        params: {
          form,
          taxYear: String(taxYear),
          fileName,
          userId,
        },
        ...AXIOS_CONFIG,
      }
    );
    if (presignedUrl.status === 200) {
      const url = presignedUrl.data.content.link;
      return url;
    }
  } catch {
    console.error("Error fetching presigned url");
  }
};

export const archiveDocument = async (taxYear: number, s3Key: string) => {
  try {
    const response = await axios.delete(
      `${backend_url}/${Endpoints.presignedUrl}`,
      {
        data: {
          taxYear: String(taxYear),
          s3Key: s3Key,
        },
        ...AXIOS_CONFIG,
      }
    );
    if (response.status === 200) {
      return response.data.content;
    }
  } catch {
    console.error("Error archiving document");
  }
};
/**
 *
 * @deprecated Use useDocumentUpload hook instead.
 * This presigned URL upload pattern has been replaced with direct base64 JSON upload.
 * All TaxOrganizer components have been migrated to useDocumentUpload.
 *
 * @see /utils/hooks/useDocumentUpload.ts
 * @param data a JSON object
 * @param fileDetails an array of file details
 * @param file a file
 * uploads a document
 */
export const uploadDocument = async (data: object, file: File | null) => {
  console.warn(
    "[DEPRECATED] uploadDocument() uses presigned URLs. Use useDocumentUpload hook instead. " +
      "See /utils/hooks/useDocumentUpload.ts for the new implementation."
  );

  try {
    const presignedUrl = await axios.put(
      `${backend_url}/${Endpoints.presignedUrl}`,
      data,
      {
        ...AXIOS_CONFIG,
      }
    );

    // upload to s3 if presigned url is retreived successfully
    if (presignedUrl.status === 200) {
      const config = {
        headers: {
          "Content-Type": file?.type,
        },
      };
      // load the file
      const reader = new FileReader();
      reader.onload = (event) => {
        const arrayBuffer = event.target?.result;
        // Send a PUT request to the presigned URL with the file data
        axios.put(presignedUrl.data.content.link, arrayBuffer, config);
      };

      if (file) {
        reader.readAsArrayBuffer(file);
      }
    }
  } catch (error) {
    console.error("There was a problem with the axios operation: ", error);
  }
};

export const fetchDocumentsByYear = async (
  userId: string | undefined,
  taxYear?: number | undefined
) => {
  try {
    const response = await axios.get(`${backend_url}/${Endpoints.documents}`, {
      params: {
        userId: userId,
        taxYear: taxYear ?? undefined,
      },
      ...AXIOS_CONFIG,
    });
    return response.data.content;
  } catch (error) {
    console.error("Error fetching documents:", error);
  }
};

/**
 *
 * @param setSensitiveInfo a function to set the client's sensitive information
 * @param params a JSON object
 * @returns updates the user's sensitive information like SSN, Bank Info, etc.
 */
export const getSecureInformation = async (
  userId: string,
  taxYear?: number
): Promise<any> => {
  try {
    const response = await axios.get(`${backend_url}/${Endpoints.secureInfo}`, {
      params: {
        userId: userId,
        taxClient: true,
        spouse: true,
        dependents: true,
        ...(taxYear && { taxYear }),
      },
      ...AXIOS_CONFIG,
    });

    return response;
  } catch (error) {
    console.error("Error fetching data for sensitive info:", error);
    return [];
  }
};

/**
 *
 * @param userId a user id
 * a helper function to get and update personalInfo data
 */
export const getPersonalInfo = async (userId?: string) => {
  try {
    const response = await axios.get(
      `${backend_url}/${Endpoints.personalInfo}`,
      {
        params: {
          userId: userId,
        },
        ...AXIOS_CONFIG,
      }
    );
    localStorage.setItem("personalInfo", JSON.stringify(response.data.content));
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const updatePersonalInfo = async (content: object) => {
  try {
    const response = await axios.put(
      `${backend_url}/${Endpoints.personalInfo}`,
      content,
      {
        ...AXIOS_CONFIG,
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

export const getOnboardingStatus = async () => {
  try {
    const response = await axios.get(`${backend_url}/api/onboarding/`, {
      withCredentials: true,
    });
    localStorage.setItem(
      "onboardingCompleted",
      JSON.stringify(response.data.content.data.onboardingProgress.isCompleted)
    );
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/**
 * Handles user data storage and store initialization after successful login
 * @param loginContent The authenticated session payload returned by login.
 */
const handleUserDataStorage = async (
  loginContent: LoginResponse["content"]
) => {
  const { user, billingContext } = loginContent;
  const { setAuthSession } = useAuthStore.getState();
  const { setRole } = useAuthStore.getState();

  // Validate response structure before accessing properties
  if (user.id) {
    console.log("Initializing user stores after successful login...");

    // 1. Initialize auth store with user data and ensure loading is false
    setAuthSession(user, billingContext ?? null);
    setRole(user.role);
    // Explicitly set loading to false to complete auth store initialization
    useAuthStore.setState({ loading: false });
    console.log("Auth store initialized with user:", user.email);

    // 2. Store user info in localStorage for persistence
    localStorage.setItem("userInfo", JSON.stringify(user));

    // 4. Check onboarding status
    try {
      await fetchOnboardingStatus();
    } catch (error) {
      console.error("Failed to fetch onboarding status:", error);
      // Continue with login process even if onboarding status fails
    }

    console.log("Store initialization completed successfully");
  } else {
    console.error("Invalid user data structure in login response:", user);
    storeEmptyUserData();
  }
};

/**
 * Fetches and stores the user's onboarding status
 */
const fetchOnboardingStatus = async () => {
  try {
    const onboardingResponse = await axios.get(
      `${backend_url}/api/onboarding/`,
      {
        ...AXIOS_CONFIG,
      }
    );

    if (onboardingResponse.data?.data) {
      localStorage.setItem(
        "onboardingCompleted",
        JSON.stringify(onboardingResponse.data.data.isCompleted || false)
      );
    }
  } catch (onboardingError) {
    console.error("Error checking onboarding status:", onboardingError);
    // Continue with login process even if fetching onboarding status fails
  }
};

/**
 * Stores empty user data in case of errors
 */
const storeEmptyUserData = () => {
  localStorage.setItem("userInfo", JSON.stringify({}));
  localStorage.setItem("onboardingCompleted", JSON.stringify(false));
};

/**
 * Handles login error notifications
 * @param error The error object
 * @param data The login data
 * @param setNotification The notification setter function
 */
const handleLoginError = (
  error: any,
  data: any,
  setNotification?: (
    value: React.SetStateAction<{
      title: string;
      message: string | ReactNode;
      color: string;
      loading?: boolean;
    } | null>
  ) => void
) => {
  if (error.response?.status === 401) {
    console.error("login: ", error.response);

    switch (error.response?.data?.message) {
      case "Subscription not active":
        // Redirect to stripe page if the user signing up as owner
        window.location.href = stripe_url + "?prefilled_email=" + data?.email;
        break;
      case "Email not verified":
        return { error: "email_not_verified", email: data?.email };
      case "User is archived":
        setNotification &&
          setNotification({
            title: "Account Archived",
            message:
              "Your access has been removed. Contact support for assistance.",
            color: "orange",
            loading: false,
          });
        break;
      default:
        setNotification &&
          setNotification({
            title: "Incorrect email or password",
            message: "Please try again with correct email and password",
            color: "red",
            loading: false,
          });
        break;
    }
  } else {
    setNotification &&
      setNotification({
        title: "An error occurred while trying to sign you in",
        message:
          "We apologize. Currently the site is down. Please click Need Help and fill out a ticket. Then, try again later.",
        color: "red",
        loading: false,
      });
  }
};

/**
 * sets the access token and refresh token and logs the user in
 * sets the notification to display upon successful login, errors, and loading
 */
export const login = async (
  data: object,
  setNotification?: (
    value: React.SetStateAction<{
      title: string;
      message: string | ReactNode;
      color: string;
      loading?: boolean | undefined;
    } | null>
  ) => void
) => {
  try {
    // Show loading notification
    setNotification &&
      setNotification({
        title: "Logging in",
        message: "Please wait while we log you in...",
        color: "blue",
        loading: true,
      });

    // Make login request
    const response = await axios.post(
      `${backend_url}/${Endpoints.authentication.login}`,
      data,
      {
        ...AXIOS_CONFIG,
      }
    );

    if (response.status === 200) {
      // Clear all localStorage and sessionStorage
      sessionStorage.clear();
      localStorage.clear();

      // Fetch user info
      try {
        const loginResponse = response.data as LoginResponse;
        await handleUserDataStorage(loginResponse.content);
      } catch (error) {
        console.error("Error fetching user data after login:", error);
        storeEmptyUserData();
      }

      // Show success notification
      setNotification &&
        setNotification({
          title: "Login Successful",
          message: "You have successfully logged in",
          color: "green",
          loading: false,
        });

      window.location.href = "/"; // Redirect to home page after successful login
    }

    return response.data;
  } catch (error) {
    console.error("There was a problem with the axios operation: ", error);
    return handleLoginError(error, data, setNotification);
  }
};

/**
 * a helper function to logout the user
 */
export const logout = async () => {
  // Import the setLoggingOut function
  const { setLoggingOut } = await import("@/interceptors/axiosIntercepter");

  // Set flag to prevent axios interceptor from refreshing tokens
  setLoggingOut(true);

  // Shutdown Intercom before logout to clear user session
  try {
    const { shutdownIntercom } = await import("@/utils/intercom");
    shutdownIntercom();
  } catch (error) {
    console.error("Error shutting down Intercom during logout:", error);
    // Continue with logout even if Intercom shutdown fails
  }

  // Get redirect path before clearing storage
  const businessId = localStorage.getItem("businessId");
  const businessInfo = JSON.parse(localStorage.getItem("businessInfo") || "{}");
  const vanitySlug = businessInfo.vanitySlug;
  let slug = vanitySlug ? vanitySlug : businessId;
  const redirectPath = slug ? `/${slug}${AppRoutes.login}` : AppRoutes.login;

  // Clear storage first
  sessionStorage.clear();
  localStorage.clear();

  try {
    // Wait for backend to clear cookies before redirecting
    await fetch(`${backend_url}/${Endpoints.authentication.logout}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
  } catch (error) {
    // Log error but continue with redirect
    console.error("Error during logout:", error);
  }

  // Hard redirect after backend has cleared cookies
  window.location.replace(redirectPath);
};

/**
 *
 * @param oldPassword a string
 * @param newPassword a string
 * returns a response after changing password
 */
export const changePassword = async (
  oldPassword: string,
  newPassword: string
) => {
  try {
    const response = await axios.post(
      `${backend_url}/${Endpoints.authentication.changePassword}`,
      { oldPassword, newPassword },
      {
        ...AXIOS_CONFIG,
      }
    );
    return response;
  } catch (error) {
    console.error("Error changing password:", error);
    throw error; // Re-throw the error so it can be caught by the component
  }
};

/**
 * Sends a reset password email to the user.
 * @param email - The user's email address.
 * @param businessSlug - Optional business slug for business-scoped password reset.
 */
export const sendResetPasswordEmail = async (
  email: string,
  businessSlug?: string
) => {
  try {
    const body: { email: string; businessSlug?: string } = { email };
    if (businessSlug) {
      body.businessSlug = businessSlug;
    }
    const response = await axios.post(
      `${backend_url}/${Endpoints.authentication.resetPassword}`,
      body,
      {
        ...AXIOS_CONFIG,
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/**
 *
 * @param token a string
 * checks if the token is valid
 */
export const checkIfResetPasswordTokenValid = async (token: string | null) => {
  try {
    const response = await axios.get(
      `${backend_url}/${Endpoints.authentication.updatePassword}${token}/`,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/**
 *
 * @param token a string
 * @param newPassword a new password
 * @returns a response after updating password
 */
export const updatePassword = async (
  token: string | null,
  newPassword: string
) => {
  try {
    const response = await axios.post(
      `${backend_url}/${Endpoints.authentication.updatePassword}`,
      { newPassword, token },
      {
        ...AXIOS_CONFIG,
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/* POST: api/users/ */
/**
 *
 * a helper function to add user
 */
export const sendUserData = async (content: object) => {
  try {
    const response = await axios.post(
      `${backend_url}${Endpoints.user}`,
      content,
      {
        ...AXIOS_CONFIG,
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
    if (
      (error as any).response &&
      (error as any).response.status >= 400 &&
      (error as any).response.status < 500
    ) {
      // Return the error response message for 4xx errors
      return {
        error: true,
        message:
          (error as any).response.data?.message ??
          "An error occurred. Please try again.",
      };
    }
    console.error("Error fetching data:", error);
    return {
      error: true,
      message: "An unexpected error occurred. Please try again later.",
    };
  }
};

/* POST: api/users/ */
/**
 *
 * a helper function to add user
 */
export const getNotifications = async (params: Object) => {
  try {
    console.log("[API] getNotifications called with params:", params);

    const fullUrl = `${backend_url}${Endpoints.notification}`;
    const response = await axios.get(fullUrl, {
      params: params,
      ...AXIOS_CONFIG,
    });
    if (response?.data?.content) {
      localStorage.setItem("notifications", JSON.stringify(response?.data));
    }
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
  return null;
};

export const readNotification = async (
  id: number | number[],
  archive?: boolean
) => {
  try {
    const response = await axios.put(
      `${backend_url}${Endpoints.notification}`,
      {
        notificationIds: typeof id === "number" ? [id] : id,
        archive: archive,
      },
      {
        ...AXIOS_CONFIG,
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

// SPOUSEINFO

/**
 * Retrieves spouse information for a given user ID by making an async request to the backend.
 *
 * @param {string} userId - The ID of the user to retrieve spouse information for.
 * @return {Promise} The response containing the spouse information.
 */
export const getSpouseInfo = async (userId?: string): Promise<any> => {
  try {
    const response = await axios.get(`${backend_url}/${Endpoints.spouseInfo}`, {
      params: {
        userId,
      },
      ...AXIOS_CONFIG,
    });
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
  return null;
};

/**
 * Updates spouse information for a user.
 *
 * @param {Object} content - the updated spouse information
 * @param {string} userId - (optional) the user ID to update spouse information for
 * @return {Promise} the response from the server after updating spouse information
 */
export const updateSpouseInfo = async (
  content: object,
  userId?: string
): Promise<any> => {
  try {
    const response = await axios.put(
      `${backend_url}/${Endpoints.spouseInfo}${userId}/`,
      content,
      {
        ...AXIOS_CONFIG,
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/**
 * Function to add spouse information to the backend.
 *
 * @param {Object} content - the spouse information to be added
 * @return {Promise} the response from the backend
 */
export const addSpouseInfo = async (content: object): Promise<any> => {
  try {
    const response = await axios.post(
      `${backend_url}/${Endpoints.spouseInfo}`,
      content,
      {
        ...AXIOS_CONFIG,
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

// DEPENDENT INFO

/**
 * Retrieves information about a dependent using their ID.
 *
 * @param {string} dependentId - The ID of the dependent to retrieve information for.
 * @return {Promise} The response containing the dependent information.
 */
export const getDependentInfo = async (
  dependentNum: number,
  userId: string
): Promise<any> => {
  try {
    const response = await axios.get(
      `${backend_url}/${Endpoints.dependentInfo}`,
      {
        params: {
          userId: userId,
          dependentNum: dependentNum,
        },
        ...AXIOS_CONFIG,
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
  return null;
};

/**
 * Updates dependent information by making a PUT request to the backend API.
 *
 * @param {Object} content - The content to be updated.
 * @param {string} dependentId - (Optional) The ID of the dependent to be updated.
 * @return {Promise<any>} The response from the API after updating the dependent information.
 */
export const updateDependentInfo = async (
  content: object,
  userId: string,
  dependentNum: number
): Promise<any> => {
  try {
    const response = await axios.put(
      `${backend_url}/${Endpoints.dependentInfo}`,
      content,
      {
        params: {
          userId: userId,
          dependentNum: dependentNum,
        },
        ...AXIOS_CONFIG,
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/**
 * Function to add dependent information.
 *
 * @param {Object} content - the content to be added
 * @return {Promise<any>} the response from the server
 */
export const addDependentInfo = async (content: object): Promise<any> => {
  try {
    const response = await axios.post(
      `${backend_url}/${Endpoints.dependentInfo}`,
      content,
      {
        ...AXIOS_CONFIG,
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

// Docs to Upload

export const getDocsToUpload = async (userId: string): Promise<any> => {
  try {
    const response = await axios.get(
      `${backend_url}/${Endpoints.clients.docsToUpload}${userId}/`,
      {
        ...AXIOS_CONFIG,
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

// VERIFY EMAIL
/**
 * Function to verify email.
 *
 * @param {String} token - the content to be added
 * @return {Promise<any>} the response from the server
 */
export const verifyEmail = async (
  token: string,
  setNotification: any,
  onSuccess: () => void
): Promise<any> => {
  try {
    const response = await axios.post(
      `${backend_url}/${Endpoints.authentication.verifyEmail}`,
      { token: token },
      {
        ...AXIOS_CONFIG,
      }
    );

    if (response.status === 200) {
      onSuccess();
    } else if (response.status === 202) {
      setNotification({
        title: "Email Already Verified",
        message:
          "Email verified successfully. Redirecting to the login page in 5 seconds...",
        color: "orange",
      });
    } else {
      const errorResponse: any = response;

      if (errorResponse.response.status === 404) {
        setNotification({
          title: "Email Already Verified",
          message:
            "This link has already been used. If you are unable to login contact support: help@smartwiz.io.",
          color: "orange",
        });
      } else {
        setNotification({
          title: "Email Not Verified",
          message:
            "Email verification failed. Please try resend verification or contact support: help@smartwiz.io.",
          color: "red",
        });
      }
    }
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

/**
 * Function to reverify email.
 *
 * @param {String} token - the content to be added
 * @return {Promise<any>} the response from the server
 */

export interface ResendEmailPayload {
  email?: string;
  token?: string;
}

export const ResendEmail = async (
  payload: ResendEmailPayload,
  setNotification: any
): Promise<any> => {
  try {
    const response = await axios.post(
      `${backend_url}/${Endpoints.authentication.resendEmail}`,
      payload,
      {
        ...AXIOS_CONFIG,
      }
    );
    if (response.status === 200) {
      setNotification({
        title: "Email Resend Succesfully",
        message: "Please check your email to verify your account",
        color: "green",
      });
    } else {
      setNotification({
        title: "Resend Email Failed",
        message:
          "Email resend failed. Please try again later or contact support: help@smartwiz.io.",
        color: "red",
      });
      return response;
    }
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

/**
 * Resends the verification email based on the provided checkout ID.
 *
 * @async
 * @function getVerificationResendEmail
 * @param {string} checkoutId - The ID associated with the checkout process.
 * @returns {Promise<any>} A promise that resolves to the response from the backend.
 * @throws Will throw an error if the checkoutId is not provided or if the request fails.
 */
export const getVerificationResendEmail = async (
  checkoutId: string
): Promise<any> => {
  if (!checkoutId) {
    throw new Error("Invalid checkoutId: checkoutId is required.");
  }

  try {
    const params = { checkoutid: checkoutId };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
      params,
      timeout: 10000, // Timeout for the request, 10 seconds, prevents the request from hanging indefinitely,
    };

    const response = await axios.get(
      `${backend_url}/${Endpoints.resendEmail}`,
      config
    );

    if (response.status !== 200) {
      throw new Error(`Unexpected response status: ${response.status}`);
    }

    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(
        "Axios error fetching data:",
        error.message,
        error.response?.data
      );
      throw new Error(`Request failed: ${error.message}`);
    } else {
      console.error("Unexpected error fetching data:", error);
      throw error;
    }
  }
};

/**
 * Fetches the notification settings from the backend.
 *
 * @async
 * @function getNotificationSetings
 * @returns {Promise<any>} A promise that resolves to the response from the backend.
 * @throws Will throw an error if the request fails.
 */
export const getNotificationSetings = async (): Promise<any> => {
  try {
    const response = await axios.get(
      `${backend_url}${Endpoints.notificationSetting}`,
      {
        ...AXIOS_CONFIG,
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/**
 * Updates the notification settings in the backend.
 *
 * @async
 * @function updateNotificationSetings
 * @param {Object} content - The content to update the notification settings with.
 * @returns {Promise<any>} A promise that resolves to the response from the backend.
 * @throws Will throw an error if the request fails.
 */
export const updateNotificationSetings = async (content: object) => {
  try {
    const response = await axios.put(
      `${backend_url}${Endpoints.notificationSetting}`,
      content,
      {
        ...AXIOS_CONFIG,
      }
    );
    if (response.status !== 200) {
      console.error("Failed to update code");
    }
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/**
 * get analytics data for Tax Pro
 */
export const getTaxProAnalytics = async (params: { age: number }) => {
  try {
    const response = await axios.get(`${backend_url}/api/analytics/taxpro/`, {
      params,
      ...AXIOS_CONFIG,
    });
    return response.data.content; // Adjust based on actual API response
  } catch (error) {
    console.error("Error fetching analytics:", error);
    throw error;
  }
};

/**
 * a helper function to get billing information
 */
export const getBillingInformation = async (
  return_url?: string,
  type?: string,
  skip_portal?: boolean
) => {
  try {
    return await axios.get(`${backend_url}/${Endpoints.billing}`, {
      params: {
        return_url,
        type,
        ...(skip_portal ? { skip_portal: "true" } : {}),
      },
      ...AXIOS_CONFIG,
    });
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/**
 * Get subscription information without creating billing portal sessions.
 * This is a lightweight endpoint suitable for polling and frequent queries.
 * Returns current plan details, credit balance, and subscription status.
 */
export const getSubscriptionInfo = async () => {
  try {
    return await axios.get(`${backend_url}/${Endpoints.subscriptionInfo}`, {
      ...AXIOS_CONFIG,
    });
  } catch (error) {
    console.error("Error fetching subscription info:", error);
    throw error;
  }
};

export const getMembers = () => {
  try {
    return axios.get(`${backend_url}/${Endpoints.members}`, {
      ...AXIOS_CONFIG,
    });
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/**
 * adds a team member to the business
 * @param content a JSON object containing the data to be sent to the backend
 * @returns a response that contains the status code, message, and data
 */
export const addMemberToBusiness = async (content: object) => {
  try {
    const response = await axios.post(
      `${backend_url}/${Endpoints.members}`,
      content,
      {
        ...AXIOS_CONFIG,
      }
    );
    return response;
  } catch (error) {
    console.error("Error creating data:", error);
  }
};

/**
 * changes the role of a business team member as long as they are not an owner
 * @param content a JSON object containing the data to be sent to the backend
 * @returns a response that contains the status code, message, and data
 */
export const changeRole = async (content: object) => {
  try {
    return await axios.put(`${backend_url}/${Endpoints.members}`, content, {
      ...AXIOS_CONFIG,
    });
  } catch (error) {
    console.error("Error creating data:", error);
  }
};

export const assignClientsToMembers = async (content: object) => {
  try {
    return await axios.post(`${backend_url}/${Endpoints.assignment}`, content, {
      ...AXIOS_CONFIG,
    });
  } catch (error) {
    console.error("Error creating data:", error);
  }
};

/**
 * Archive or unarchive a client by setting the is_archived property
 * @param clientId - The client ID to archive/unarchive
 * @param archive - True to archive, false to unarchive
 */
export const archiveClient = async (
  clientId: string,
  archive: boolean = true
): Promise<void> => {
  try {
    await axios.post(
      `${backend_url}/${Endpoints.clientArchive}${clientId}/`,
      { archive },
      {
        ...AXIOS_CONFIG,
      }
    );
  } catch (error) {
    console.error(
      `Error ${archive ? "archiving" : "unarchiving"} client:`,
      error
    );
    throw error;
  }
};
